// src/products/dataroom/services/dataroomInviteIntegration.js
// Enhanced integration between UserInvites and Dataroom parties/teams/permissions

import { supabase } from '../../../config';
import { userInvitesApi } from '../../userInvites/api/userInvitesApi';

class DataroomInviteIntegration {
  
  /**
   * Process a new user invitation with dataroom party/team assignment
   * This is called when a user accepts an invite via UserInvites
   * @param {Object} inviteData - { userId, projectId, role, partyId, teamIds }
   */
  async processDataroomInvite(inviteData) {
    try {
      const { userId, projectId, role, partyId, teamIds = [] } = inviteData;
      
      // 1. Grant basic dataroom access
      const accessResult = await userInvitesApi.grantDataroomAccess(userId, projectId, role);
      if (accessResult.status !== 'success') {
        throw new Error(`Failed to grant dataroom access: ${accessResult.message}`);
      }

      // 2. Assign user to party (required)
      if (partyId) {
        const partyResult = await this.assignUserToParty(userId, projectId, partyId);
        if (partyResult.status !== 'success') {
          console.warn(`Failed to assign user to party: ${partyResult.message}`);
        }
      }

      // 3. Assign user to specified teams (within the party)
      if (teamIds && teamIds.length > 0) {
        const teamResult = await this.assignUserToTeams(userId, projectId, teamIds);
        if (teamResult.status !== 'success') {
          console.warn(`Failed to assign user to teams: ${teamResult.message}`);
        }
      }

      // 4. Set default permissions based on role and teams
      const permissionResult = await this.setDefaultPermissions(userId, projectId, role, teamIds);
      if (permissionResult.status !== 'success') {
        console.warn(`Failed to set default permissions: ${permissionResult.message}`);
      }

      return {
        status: 'success',
        data: {
          dataroomAccess: accessResult.data,
          partyAssignment: partyId,
          teamAssignments: teamIds,
          permissionsSet: permissionResult.data
        },
        message: 'User successfully integrated with dataroom parties, teams and permissions'
      };

    } catch (error) {
      console.error('Error processing dataroom invite:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Assign user to a party in a dataroom project
   */
  async assignUserToParty(userId, projectId, partyId) {
    try {
      const { error } = await supabase
        .from('dataroom_user_parties')
        .upsert({
          user_id: userId,
          project_id: projectId,
          party_id: partyId
        }, { onConflict: 'user_id,project_id,party_id' })
        .select();

      if (error) throw error;

      return {
        status: 'success',
        message: `User assigned to party`
      };

    } catch (error) {
      console.error('Error assigning user to party:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Assign user to multiple teams in a dataroom project
   */
  async assignUserToTeams(userId, projectId, teamIds) {
    try {
      const assignments = teamIds.map(teamId => ({
        user_id: userId,
        team_id: teamId,
        project_id: projectId
      }));

      const { error } = await supabase
        .from('dataroom_user_teams')
        .upsert(assignments, { onConflict: 'user_id,project_id,team_id' })
        .select();

      if (error) throw error;

      return {
        status: 'success',
        data: assignments,
        message: `User assigned to ${teamIds.length} teams`
      };

    } catch (error) {
      console.error('Error assigning user to teams:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Set default permissions for a user based on their role and teams
   */
  async setDefaultPermissions(userId, projectId, role, teamIds) {
    try {
      // Get all items in the project
      const { data: items, error: itemsError } = await supabase
        .from('dataroom_items')
        .select('id')
        .eq('project_id', projectId);

      if (itemsError) throw itemsError;

      // Determine default permission type based on role
      let defaultPermissionType = 'view';
      if (role === 'creator' || role === 'support') {
        defaultPermissionType = 'download';
      }

      // Create permissions for all items (Teams-based)
      // Assign to all teams the user belongs to
      const permissions = [];
      teamIds.forEach(teamId => {
        items.forEach(item => {
          permissions.push({
            team_id: teamId,
            dataroom_item_id: item.id,
            permission_type: defaultPermissionType
          });
        });
      });

      if (permissions.length > 0) {
        const { error: permError } = await supabase
          .from('dataroom_file_permissions')
          .upsert(permissions, { onConflict: 'team_id,dataroom_item_id' });

        if (permError) throw permError;
      }

      return {
        status: 'success',
        data: permissions,
        message: `Default permissions set for ${items.length} items across ${teamIds.length} teams`
      };

    } catch (error) {
      console.error('Error setting default permissions:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Get or create default party and team for a project
   * Returns the default team ID for use in invites
   */
  async getOrCreateDefaultPartyAndTeam(projectId) {
    try {
      // Check if default party exists
      const { data: existingParty, error: partyCheckError } = await supabase
        .from('dataroom_parties')
        .select('id, name')
        .eq('project_id', projectId)
        .eq('name', 'Default Party')
        .single();

      if (partyCheckError && partyCheckError.code !== 'PGRST116') {
        throw partyCheckError;
      }

      let partyId;
      if (existingParty) {
        partyId = existingParty.id;
      } else {
        // Create new default party
        const { data: newParty, error: createPartyError } = await supabase
          .from('dataroom_parties')
          .insert({
            name: 'Default Party',
            description: 'Default party for project viewers',
            project_id: projectId,
            party_type: 'custom',
            code: 'P01',
            color: '#3B82F6'
          })
          .select()
          .single();

        if (createPartyError) throw createPartyError;
        partyId = newParty.id;
      }

      // Check if default team exists in this party
      const { data: existingTeam, error: teamCheckError } = await supabase
        .from('dataroom_teams')
        .select('id, name')
        .eq('party_id', partyId)
        .eq('name', 'Default Team')
        .single();

      if (teamCheckError && teamCheckError.code !== 'PGRST116') {
        throw teamCheckError;
      }

      let teamId;
      if (existingTeam) {
        teamId = existingTeam.id;
      } else {
        // Create new default team
        const { data: newTeam, error: createTeamError } = await supabase
          .from('dataroom_teams')
          .insert({
            name: 'Default Team',
            description: 'Default team for project viewers',
            party_id: partyId,
            team_type: 'general',
            code: 'T01'
          })
          .select()
          .single();

        if (createTeamError) throw createTeamError;
        teamId = newTeam.id;
      }

      return {
        status: 'success',
        data: { partyId, teamId },
        message: 'Default party and team ready'
      };

    } catch (error) {
      console.error('Error getting/creating default party and team:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Enhanced invite function that includes dataroom party/team assignment
   */
  async sendDataroomInviteWithTeams(inviteData) {
    try {
      const { 
        email, 
        projectId, 
        role = 'viewer', 
        partyId,
        teamIds = [], 
        customMessage,
        invitedBy 
      } = inviteData;

      // 1. Ensure we have a party and at least one team (create default if needed)
      let finalPartyId = partyId;
      let finalTeamIds = teamIds;
      
      if (!finalPartyId || finalTeamIds.length === 0) {
        const defaultResult = await this.getOrCreateDefaultPartyAndTeam(projectId);
        if (defaultResult.status === 'success') {
          finalPartyId = finalPartyId || defaultResult.data.partyId;
          if (finalTeamIds.length === 0) {
            finalTeamIds = [defaultResult.data.teamId];
          }
        }
      }

      // 2. Create the invite via UserInvites API
      const inviteResult = await userInvitesApi.createInvite({
        email,
        inviteforproduct: 'dataroom',
        role,
        projectid: projectId,
        invitedby: invitedBy,
        custom_message: customMessage,
        metadata: {
          partyId: finalPartyId,
          teamIds: finalTeamIds,
          autoAssignTeams: true
        }
      });

      if (inviteResult.status !== 'success') {
        throw new Error(`Failed to create invite: ${inviteResult.message}`);
      }

      // 3. Update dataroom_users status to 'invited' if user exists
      await this.updateUserInviteStatus(email, projectId, 'invited');

      return {
        status: 'success',
        data: {
          ...inviteResult.data,
          assignedParty: finalPartyId,
          assignedTeams: finalTeamIds,
          magicLink: inviteResult.data.magic_link_url
        },
        message: 'Dataroom invite sent with party/team assignments'
      };

    } catch (error) {
      console.error('Error sending dataroom invite with teams:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Handle invite acceptance - called when user clicks magic link
   */
  async handleInviteAcceptance(inviteToken, userId) {
    try {
      // 1. Get invite details
      const inviteResult = await userInvitesApi.getInviteByToken(inviteToken);
      if (inviteResult.status !== 'success' || !inviteResult.data) {
        throw new Error('Invalid or expired invite');
      }

      const invite = inviteResult.data;

      // 2. Process dataroom integration if it's a dataroom invite
      if (invite.inviteforproduct === 'dataroom' && invite.projectid) {
        const partyId = invite.metadata?.partyId;
        const teamIds = invite.metadata?.teamIds || [];
        
        const integrationResult = await this.processDataroomInvite({
          userId,
          projectId: invite.projectid,
          role: invite.role,
          partyId,
          teamIds,
          customMessage: invite.custom_message
        });

        if (integrationResult.status !== 'success') {
          console.warn('Dataroom integration failed:', integrationResult.message);
        }

        // 3. Update dataroom_users to link email to real user_id
        await this.linkEmailToUserId(invite.email, userId, invite.projectid);
      }

      // 4. Mark invite as accepted
      const acceptResult = await userInvitesApi.acceptInvite(invite.id, userId);
      if (acceptResult.status !== 'success') {
        throw new Error(`Failed to accept invite: ${acceptResult.message}`);
      }

      return {
        status: 'success',
        data: {
          invite: acceptResult.data,
          dataroomIntegration: invite.inviteforproduct === 'dataroom'
        },
        message: 'Invite accepted and dataroom access granted'
      };

    } catch (error) {
      console.error('Error handling invite acceptance:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Link pending email-based user to real auth user_id
   */
  async linkEmailToUserId(email, userId, projectId) {
    try {
      const { error } = await supabase
        .from('dataroom_users')
        .update({
          user_id: userId,
          status: 'active',
          last_active: new Date().toISOString()
        })
        .eq('email', email)
        .eq('project_id', projectId)
        .is('user_id', null); // Only update if user_id is still null

      if (error) throw error;

      return {
        status: 'success',
        message: 'User linked successfully'
      };
    } catch (error) {
      console.error('Error linking email to user_id:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Update user invite status in dataroom_users when invite is sent
   */
  async updateUserInviteStatus(email, projectId, status) {
    try {
      const { error } = await supabase
        .from('dataroom_users')
        .update({
          status: status,
          invited_at: new Date().toISOString()
        })
        .eq('email', email)
        .eq('project_id', projectId);

      if (error) throw error;

      return {
        status: 'success',
        message: 'User status updated'
      };
    } catch (error) {
      console.error('Error updating user invite status:', error);
      return {
        status: 'error',
        message: error.message
      };
    }
  }

  /**
   * Get available parties and teams for a project (for invite forms)
   */
  async getAvailablePartiesAndTeams(projectId) {
    try {
      // Get all parties for the project
      const { data: parties, error: partiesError } = await supabase
        .from('dataroom_parties')
        .select(`
          id,
          name,
          description,
          code,
          color
        `)
        .eq('project_id', projectId)
        .order('name');

      if (partiesError) throw partiesError;

      // Get all teams for these parties
      const partyIds = (parties || []).map(p => p.id);
      const { data: teams, error: teamsError } = await supabase
        .from('dataroom_teams')
        .select(`
          id,
          name,
          description,
          party_id
        `)
        .in('party_id', partyIds)
        .order('name');

      if (teamsError) throw teamsError;

      // Group teams by party
      const partiesWithTeams = (parties || []).map(party => ({
        ...party,
        teams: (teams || []).filter(team => team.party_id === party.id)
      }));

      return {
        status: 'success',
        data: partiesWithTeams
      };

    } catch (error) {
      console.error('Error getting available parties and teams:', error);
      return {
        status: 'error',
        message: error.message,
        data: []
      };
    }
  }

  /**
   * @deprecated Use getAvailablePartiesAndTeams instead
   * Legacy method for backward compatibility
   */
  async getAvailableGroups(projectId) {
    console.warn('getAvailableGroups is deprecated. Use getAvailablePartiesAndTeams instead.');
    const result = await this.getAvailablePartiesAndTeams(projectId);
    // Transform to old format for backward compatibility
    if (result.status === 'success') {
      const allTeams = result.data.flatMap(party => 
        party.teams.map(team => ({
          id: team.id,
          name: `${party.name} - ${team.name}`,
          description: team.description,
          partyId: party.id,
          partyName: party.name
        }))
      );
      return {
        status: 'success',
        data: allTeams
      };
    }
    return result;
  }
}

export const dataroomInviteIntegration = new DataroomInviteIntegration();
